import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCurrency } from "../App";
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Mail,
  Globe,
  ArrowRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const MobileMenu = ({ isOpen, services }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        className="lg:hidden bg-white border-t border-slate-100 overflow-hidden"
        data-testid="mobile-menu"
      >
        <div className="container-custom py-4 space-y-4">
          <Link to="/about" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-about">About</Link>
          <div>
            <span className="block py-2 text-slate-700 font-medium">Services</span>
            <div className="pl-4 space-y-2 mt-2">
              {services.map((service) => (
                <Link key={service.slug} to={`/services/${service.slug}`} className="block py-1 text-slate-600 hover:text-optima-blue" data-testid={`mobile-nav-service-${service.slug}`}>{service.name}</Link>
              ))}
            </div>
          </div>
          <Link to="/software" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-software">Software</Link>
          <Link to="/pricing" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-pricing">Pricing</Link>
          <Link to="/blog" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-blog">Blog</Link>
          <Link to="/resources" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-resources">Resources</Link>
          <Link to="/client-onboarding" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-onboarding">Client Onboarding</Link>
          <Link to="/contact" className="block py-2 text-slate-700 font-medium" data-testid="mobile-nav-contact">Contact</Link>
          <div className="pt-4 flex flex-col gap-3">
            <a href="https://calendly.com/optimagrowth-info/15min" target="_blank" rel="noopener noreferrer" className="btn-primary text-center" data-testid="mobile-cta-btn">Book a Free Call</a>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const DesktopNavigation = ({ services, resources }) => (
  <div className="hidden lg:flex items-center gap-1">
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <Link to="/about" className="px-4 py-2 text-slate-700 hover:text-optima-blue transition-colors font-medium" data-testid="nav-about">About</Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="px-4 py-2 text-slate-700 hover:text-optima-blue transition-colors font-medium bg-transparent" data-testid="nav-services-trigger">Services</NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[600px] p-6">
              <div className="grid grid-cols-2 gap-3">
                {services.map((service) => (
                  <Link key={service.slug} to={`/services/${service.slug}`} className="group flex flex-col p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`nav-service-${service.slug}`}>
                    <span className="font-medium text-slate-900 group-hover:text-optima-blue transition-colors">{service.name}</span>
                    <span className="text-sm text-slate-500">{service.desc}</span>
                  </Link>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Link to="/services" className="flex items-center gap-2 text-optima-blue font-medium hover:gap-3 transition-all" data-testid="nav-all-services">View all services <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/software" className="px-4 py-2 text-slate-700 hover:text-optima-blue transition-colors font-medium" data-testid="nav-software">Software</Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/pricing" className="px-4 py-2 text-slate-700 hover:text-optima-blue transition-colors font-medium" data-testid="nav-pricing">Pricing</Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="px-4 py-2 text-slate-700 hover:text-optima-blue transition-colors font-medium bg-transparent" data-testid="nav-resources-trigger">Resources</NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[300px] p-4">
              {resources.map((resource) => (
                <Link key={resource.href} to={resource.href} className="group flex flex-col p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`nav-resource-${resource.name.toLowerCase().replace(' ', '-')}`}>
                  <span className="font-medium text-slate-900 group-hover:text-optima-blue transition-colors">{resource.name}</span>
                  <span className="text-sm text-slate-500">{resource.desc}</span>
                </Link>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  </div>
);

const HEADER_SERVICES = [
  { name: "Business Growth Strategy", slug: "business-growth-strategy", desc: "Strategic planning & implementation" },
  { name: "Marketing Automation", slug: "marketing-automation", desc: "Automated marketing systems" },
  { name: "Sales Funnels & CRM", slug: "sales-funnels-crm", desc: "Convert leads systematically" },
  { name: "Lead Generation", slug: "lead-generation", desc: "Attract qualified prospects" },
  { name: "SEO & Content", slug: "seo-content", desc: "Organic growth systems" },
  { name: "Paid Media", slug: "paid-media", desc: "Meta & Google advertising" },
  { name: "Workflow Automation", slug: "workflow-automation", desc: "Internal efficiency systems" },
  { name: "AI-Assisted Operations", slug: "ai-operations", desc: "Smart process optimization" },
  { name: "Web & App Development", slug: "web-app-development", desc: "Custom code websites & apps" },
  { name: "ChatGPT Ads & GEO", slug: "chatgpt-ads", desc: "AI advertising & visibility" },
];

const HEADER_RESOURCES = [
  { name: "Blog", href: "/blog", desc: "Latest insights & strategies" },
  { name: "Case Studies", href: "/case-studies", desc: "Real results we've achieved" },
  { name: "Free Guides", href: "/resources", desc: "Downloadable resources" },
  { name: "Client Onboarding", href: "/client-onboarding", desc: "New client questionnaire" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { currency, setCurrency, currencies } = useCurrency();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change — intentional unconditional setState
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-soft"
          : "bg-transparent"
      }`}
      data-testid="main-header"
    >
      {/* Top bar */}
      <div className={`border-b border-slate-100 transition-all duration-300 ${isScrolled ? "py-1" : "py-2"}`}>
        <div className="container-custom flex items-center justify-between text-sm">
          <div className="hidden md:flex items-center gap-6 text-slate-600">
            <a
              href="mailto:info@optimagrowth.agency"
              className="flex items-center gap-2 hover:text-optima-blue transition-colors"
              data-testid="header-email-link"
            >
              <Mail className="w-4 h-4" />
              info@optimagrowth.agency
            </a>
            <a
              href="tel:+27661229790"
              className="flex items-center gap-2 hover:text-optima-blue transition-colors"
              data-testid="header-phone-link"
            >
              <Phone className="w-4 h-4" />
              +27 66 122 9790
            </a>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-2 text-slate-600 hover:text-optima-blue transition-colors"
                data-testid="currency-selector"
              >
                <Globe className="w-4 h-4" />
                {currency} ({currencies[currency].symbol})
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(currencies).map(([code, data]) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => setCurrency(code)}
                    className={currency === code ? "bg-slate-50" : ""}
                    data-testid={`currency-option-${code}`}
                  >
                    {data.symbol} {code} - {data.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`transition-all duration-300 ${isScrolled ? "py-3" : "py-4"}`}>
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-optima-blue to-optima-cyan flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xl">O</span>
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-bold text-xl text-optima-dark">
                OptimaGrowth
              </span>
              <span className="text-[10px] text-slate-500 -mt-1 tracking-wider uppercase">
                Growth Partner
              </span>
            </div>
          </Link>

          <DesktopNavigation services={HEADER_SERVICES} resources={HEADER_RESOURCES} />

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/contact"
              className="btn-secondary text-sm"
              data-testid="nav-contact-btn"
            >
              Contact
            </Link>
            <a
              href="https://calendly.com/optimagrowth-info/15min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm"
              data-testid="nav-cta-btn"
            >
              Book a Call
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-700"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </nav>

      <MobileMenu isOpen={isMobileMenuOpen} services={HEADER_SERVICES} />
    </header>
  );
};

export default Header;
